import * as React from "react";
import {
  Button,
  Dropdown,
  Option,
  Spinner,
  Text,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  CheckmarkCircle20Regular,
  Cloud24Regular,
  Folder24Regular,
  LinkSquare20Regular,
} from "@fluentui/react-icons";
import type { DestinationPlatform, DriveItem, FolderItem, SiteItem } from "../../types/sharepoint";
import { FolderPicker } from "./FolderPicker";

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalS },

  field: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalXXS },

  label: { fontWeight: tokens.fontWeightSemibold },

  platformRow: {
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    display: "grid",
    gap: "2px",
    gridTemplateColumns: "1fr 1fr",
    padding: "2px",
  },

  platformButton: {
    border: "0",
    minWidth: 0,
  },

  selectedPlatformButton: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    minWidth: 0,
  },

  statusRow: { alignItems: "center", display: "flex", gap: tokens.spacingHorizontalXS },

  errorText: {
    color: tokens.colorStatusDangerForeground1,
    fontSize: tokens.fontSizeBase200,
  },

  mutedText: {
    color: tokens.colorNeutralForeground3,
  },

  connectButton: { alignSelf: "flex-start" },

  selectedPath: {
    alignItems: "center",
    backgroundColor: tokens.colorStatusSuccessBackground1,
    border: `1px solid ${tokens.colorStatusSuccessBorder1}`,
    borderRadius: tokens.borderRadiusMedium,
    color: tokens.colorStatusSuccessForeground1,
    display: "grid",
    gap: tokens.spacingHorizontalXS,
    gridTemplateColumns: "auto 1fr",
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
  },

  pathText: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
});

interface UploadDestinationProps {
  destinationPlatform: DestinationPlatform;
  onSelectDestinationPlatform: (platform: DestinationPlatform) => void;

  sites: SiteItem[];
  sitesLoading: boolean;
  sitesError: string;
  selectedSite: SiteItem | undefined;
  onLoadSites: () => void;
  onLoadOneDriveFolders: () => void;
  onSelectSite: (id: string) => void;

  drives: DriveItem[];
  drivesLoading: boolean;
  drivesError: string;
  selectedDrive: DriveItem | undefined;
  onSelectDrive: (id: string) => void;

  folders: FolderItem[];
  foldersLoading: boolean;
  foldersError: string;
  selectedFolder: FolderItem | undefined;
  expandedFolders: Record<string, boolean>;
  loadingFolderIds: Record<string, boolean>;
  onSelectFolder: (node: FolderItem) => void;
  onToggleFolder: (id: string) => void;
  onExpandFolder: (node: FolderItem) => void;
}

function getDestinationPath(
  platform: DestinationPlatform,
  selectedSite?: SiteItem,
  selectedDrive?: DriveItem,
  selectedFolder?: FolderItem
): string {
  if (!selectedFolder) return "";
  if (platform === "onedrive") return `OneDrive > ${selectedFolder.name}`;

  return ["SharePoint", selectedSite?.displayName, selectedDrive?.name, selectedFolder.name]
    .filter(Boolean)
    .join(" > ");
}

export const UploadDestination: React.FC<UploadDestinationProps> = ({
  destinationPlatform,
  onSelectDestinationPlatform,
  sites,
  sitesLoading,
  sitesError,
  selectedSite,
  onLoadSites,
  onLoadOneDriveFolders,
  onSelectSite,
  drives,
  drivesLoading,
  drivesError,
  selectedDrive,
  onSelectDrive,
  folders,
  foldersLoading,
  foldersError,
  selectedFolder,
  expandedFolders,
  loadingFolderIds,
  onSelectFolder,
  onToggleFolder,
  onExpandFolder,
}) => {
  const styles = useStyles();
  const showFolderPicker = destinationPlatform === "onedrive" || !!selectedDrive;
  const destinationPath = getDestinationPath(
    destinationPlatform,
    selectedSite,
    selectedDrive,
    selectedFolder
  );

  return (
    <div className={styles.root}>
      <div className={styles.field}>
        <Text weight="semibold" size={200} className={styles.label}>
          Storage
        </Text>
        <div className={styles.platformRow} role="group" aria-label="Storage destination">
          <Button
            size="small"
            appearance="transparent"
            className={
              destinationPlatform === "sharepoint"
                ? styles.selectedPlatformButton
                : styles.platformButton
            }
            icon={<LinkSquare20Regular />}
            onClick={() => onSelectDestinationPlatform("sharepoint")}
          >
            SharePoint
          </Button>
          <Button
            size="small"
            appearance="transparent"
            className={
              destinationPlatform === "onedrive"
                ? styles.selectedPlatformButton
                : styles.platformButton
            }
            icon={<Cloud24Regular />}
            onClick={() => onSelectDestinationPlatform("onedrive")}
          >
            OneDrive
          </Button>
        </div>
      </div>

      {destinationPlatform === "sharepoint" && (
        <div className={styles.field}>
          <Text weight="semibold" size={200} className={styles.label}>
            SharePoint Site
          </Text>

          {sitesLoading ? (
            <div className={styles.statusRow}>
              <Spinner size="tiny" />
              <Text size={200}>Loading sites...</Text>
            </div>
          ) : sites.length > 0 ? (
            <Dropdown
              placeholder="Select a site"
              selectedOptions={selectedSite ? [selectedSite.id] : []}
              value={selectedSite?.displayName ?? ""}
              onOptionSelect={(_, d) => d.optionValue && onSelectSite(d.optionValue)}
              size="small"
            >
              {sites.map((site) => (
                <Option key={site.id} value={site.id}>
                  {site.displayName}
                </Option>
              ))}
            </Dropdown>
          ) : (
            <Button
              size="small"
              appearance="secondary"
              icon={<LinkSquare20Regular />}
              className={styles.connectButton}
              onClick={onLoadSites}
            >
              Load SharePoint sites
            </Button>
          )}

          {sitesError && (
            <Text className={styles.errorText} block>
              {sitesError}
            </Text>
          )}
        </div>
      )}

      {destinationPlatform === "sharepoint" && selectedSite && (
        <div className={styles.field}>
          <Text weight="semibold" size={200} className={styles.label}>
            Document Library
          </Text>

          {drivesLoading ? (
            <div className={styles.statusRow}>
              <Spinner size="tiny" />
              <Text size={200}>Loading libraries...</Text>
            </div>
          ) : (
            <Dropdown
              placeholder="Select a library"
              selectedOptions={selectedDrive ? [selectedDrive.id] : []}
              value={selectedDrive?.name ?? ""}
              onOptionSelect={(_, d) => d.optionValue && onSelectDrive(d.optionValue)}
              size="small"
              disabled={drives.length === 0}
            >
              {drives.map((drive) => (
                <Option key={drive.id} value={drive.id}>
                  {drive.name}
                </Option>
              ))}
            </Dropdown>
          )}

          {drivesError && (
            <Text className={styles.errorText} block>
              {drivesError}
            </Text>
          )}
        </div>
      )}

      {destinationPlatform === "sharepoint" && selectedSite && !selectedDrive && !drivesLoading && (
        <Text size={200} className={styles.mutedText}>
          Select a document library to continue to folders.
        </Text>
      )}

      {destinationPlatform === "onedrive" && folders.length === 0 && !foldersLoading && (
        <div className={styles.field}>
          <Button
            size="small"
            appearance="secondary"
            icon={<Cloud24Regular />}
            className={styles.connectButton}
            onClick={onLoadOneDriveFolders}
          >
            Load OneDrive folders
          </Button>
        </div>
      )}

      {showFolderPicker ? (
        <div className={styles.field}>
          <Text weight="semibold" size={200} className={styles.label}>
            Destination Folder
          </Text>

          <FolderPicker
            folders={folders}
            loading={foldersLoading}
            error={foldersError}
            selectedFolderId={selectedFolder?.id ?? ""}
            expandedFolders={expandedFolders}
            loadingFolderIds={loadingFolderIds}
            onSelectFolder={onSelectFolder}
            onToggleFolder={onToggleFolder}
            onExpandFolder={onExpandFolder}
          />
        </div>
      ) : (
        <Text size={200} className={styles.mutedText}>
          Choose where the selected emails should be saved.
        </Text>
      )}

      {selectedFolder && (
        <div className={styles.selectedPath} title={destinationPath}>
          <CheckmarkCircle20Regular />
          <Text size={200} className={styles.pathText}>
            <Folder24Regular fontSize={14} /> {destinationPath}
          </Text>
        </div>
      )}
    </div>
  );
};
